import { useState, useEffect } from "react";
import "@/App.css";
import "@/EditorPanel.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DoorOpen, Sofa, UtensilsCrossed, Bed, Bath, Droplet, Trees, Sparkles, Car, MapPin, Plus, Clock, Clock3, Clock9, X, ArrowLeft, Video, History, Save, Home, Loader2, Check, SendHorizontal, Copy, ClipboardCheck, Globe, Pencil, AlertTriangle } from "lucide-react";
import { toast, Toaster } from "sonner";
import ScriptHistory from "@/ScriptHistory";
import MarketUpdateBuilder from "@/MarketUpdateBuilder";
import "@/MarketUpdate.css";
import { useAuth } from "@/AuthContext";
import { AppHeader } from './AppHeader';
import { AvatarPicker, AvatarChip } from "@/AvatarPicker";
import SocialProOrderPicker, { useSocialProOrders, OrderGate } from "@/SocialProOrderPicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for script sections
const SECTION_ICONS = {
  "door-open": DoorOpen,
  "sofa": Sofa,
  "utensils-crossed": UtensilsCrossed,
  "bed": Bed,
  "bath": Bath,
  "droplet": Droplet,
  "trees": Trees,
  "sparkles": Sparkles,
  "car": Car,
  "map-pin": MapPin,
  "plus": Plus,
  "home": Home,
};

const getSectionIcon = (iconName) => {
  return SECTION_ICONS[iconName] || Home;
};

function ScriptBuilder({ onBack, onBuildPresentour }) {
  const { user, logout } = useAuth();
  
  // Pre-populate from user profile with smart parsing
  // "Curchods Estate Agents (Simpsons) - Woking" → Agency: "Curchods Estate Agents", Town: "Woking"
  const parseBranchName = (raw) => {
    if (!raw) return { agency: '', town: '' };
    // Split on " - " to separate agency from town
    const dashParts = raw.split(' - ');
    let agency = dashParts[0] || '';
    const town = dashParts.length > 1 ? dashParts[dashParts.length - 1].trim() : '';
    // Remove parenthetical parts like "(Simpsons)"
    agency = agency.replace(/\s*\([^)]*\)\s*/g, '').trim();
    return { agency, town };
  };
  const parsed = parseBranchName(user?.branch_name);
  const [presenterName, setPresenterName] = useState(user?.name || "");
  const [agencyName, setAgencyName] = useState(parsed.agency);
  const [townName, setTownName] = useState(parsed.town);
  
  // Checkboxes for optional fields
  const [includePresenter, setIncludePresenter] = useState(true);
  const [includeAgency, setIncludeAgency] = useState(true);
  const [includeTown, setIncludeTown] = useState(true);
  
  const [rightmoveUrl, setRightmoveUrl] = useState("");
  const [keySalesPoint, setKeySalesPoint] = useState("");
  const [descriptionTabs, setDescriptionTabs] = useState([]);
  const [activeDescriptionTab, setActiveDescriptionTab] = useState(0);
  
  // These are now fetched from admin settings
  const [agencyStyle, setAgencyStyle] = useState("");
  const [agentPersonality, setAgentPersonality] = useState("");
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  
  const [scriptLength, setScriptLength] = useState("Standard (60–90 seconds)");
  const [scriptGenerations, setScriptGenerations] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isFetchingDescription, setIsFetchingDescription] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [scriptTitle, setScriptTitle] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [loadedScriptId, setLoadedScriptId] = useState(null);
  const [inputMode, setInputMode] = useState("rightmove"); // "rightmove", "website", or "manual"
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmedScriptIndex, setConfirmedScriptIndex] = useState(null);
  // Inline editing state for individual script sections.
  // Shape: `${genIndex}-${sectionIndex}` → string (the staged edit)
  const [editingSection, setEditingSection] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [sectionWarnings, setSectionWarnings] = useState({});
  const [copiedSection, setCopiedSection] = useState(null);
  const [scriptMode, setScriptMode] = useState("property"); // "property" | "market-update"
  // Presenter avatar + WMS (Social Pro) order the script is attached to
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [selectedWmsOrder, setSelectedWmsOrder] = useState(null);
  const { data: spData, loading: spLoading, reload: reloadSpOrders } = useSocialProOrders(!!user?.branch_id || !!user?.wms_linked);
  const gatingOn = !!(spData?.linked && spData?.gating_enabled);
  const needsOrder = gatingOn && !selectedWmsOrder;
  // Order-first flow: staff can bypass; agents pick an order before the builder appears
  const [skipGate, setSkipGate] = useState(false);
  const isStaffUser = ["super_admin", "editor", "client_manager"].includes(user?.role);
  const showGate = scriptMode === 'property' && !selectedWmsOrder && !skipGate && !loadedScriptId && scriptGenerations.length === 0;

  // Arriving from "Write the script" on the landing: pick that WMS order straight away
  useEffect(() => {
    const wanted = sessionStorage.getItem('nc-preselect-wms-order');
    if (!wanted || !spData || selectedWmsOrder) return;
    const match = [...(spData.orders || []), ...(spData.all_orders || [])].find((o) => String(o.order_id) === String(wanted));
    if (match) {
      setSelectedWmsOrder(match);
      setScriptTitle((t) => t || match.address || '');
    }
    sessionStorage.removeItem('nc-preselect-wms-order');
  }, [spData, selectedWmsOrder]);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const copyAllSections = (sections) => {
    const fullText = sections.map(s => s.text).join('\n\n');
    navigator.clipboard.writeText(fullText);
    setCopiedSection('all');
    toast.success('Full script copied to clipboard');
    setTimeout(() => setCopiedSection(null), 2000);
  };

  // Fetch script settings from admin on mount
  useEffect(() => {
    const fetchScriptSettings = async () => {
      try {
        const response = await axios.get(`${API}/admin/my-script-settings`);
        setAgencyStyle(response.data.agency_style);
        setAgentPersonality(response.data.agent_personality);
        setSettingsLoaded(true);
      } catch (error) {
        console.error('Failed to fetch script settings:', error);
        // Use defaults if fetch fails
        setAgencyStyle("Friendly & Conversational");
        setAgentPersonality("Easy-going & Confident");
        setSettingsLoaded(true);
      }
    };
    fetchScriptSettings();
  }, []);

  // Submit confirmed order to editors
  const submitOrder = async (gen) => {
    if (!selectedAvatar) {
      toast.error('Please choose a presenter avatar first');
      return;
    }
    if (needsOrder) {
      toast.error('Please choose which Social Pro property this script is for');
      return;
    }
    try {
      setIsSubmitting(true);
      await axios.post(`${API}/orders/submit`, {
        property_title: scriptTitle || selectedWmsOrder?.address || gen.title || "Untitled Property",
        town: townName || "Unknown",
        script: gen.script,
        sections: gen.sections,
        avatar_id: selectedAvatar.id,
        wms_order_id: selectedWmsOrder?.order_id || null,
        settings: {
          presenter_name: presenterName,
          agency_name: agencyName,
          town_name: townName,
          key_sales_point: keySalesPoint,
          agency_style: agencyStyle,
          agent_personality: agentPersonality,
          script_length: scriptLength
        }
      });
      setConfirmedScriptIndex(activeTab);
      toast.success('Script confirmed and sent to editors!');
      setShowConfirmDialog(false);
      reloadSpOrders();
      window.dispatchEvent(new CustomEvent('nichesocial:alerts-refresh'));
    } catch (error) {
      console.error('Failed to submit order:', error);
      const detail = error?.response?.data?.detail;
      if (detail?.error === 'inappropriate_content') {
        const words = (detail.words || []).join(', ');
        toast.error(
          `Cannot submit — script contains language that isn't permitted${words ? `: ${words}` : ''}. Please edit and try again.`,
          { duration: 7000 }
        );
        // Highlight which sections are flagged
        const map = {};
        (detail.sections || []).forEach(iss => {
          map[iss.section] = iss.words;
        });
        setSectionWarnings(map);
        setShowConfirmDialog(false);
      } else if (typeof detail === 'string') {
        toast.error(detail, { duration: 7000 });
      } else {
        toast.error('Failed to submit order. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Inline editing helpers ─────────────────────────────────────────────
  const startEditSection = (genIdx, secIdx, currentText) => {
    setEditingSection(`${genIdx}-${secIdx}`);
    setEditDraft(currentText);
  };

  const cancelEditSection = () => {
    setEditingSection(null);
    setEditDraft("");
  };

  const saveSectionEdit = async (genIdx, secIdx) => {
    const newText = (editDraft || "").trim();
    if (!newText) {
      toast.error("Section can't be empty");
      return;
    }
    // Quick server-side content check before persisting
    try {
      const res = await axios.post(`${API}/scripts/check-content`, { text: newText });
      if (!res.data?.clean) {
        const words = (res.data?.words || []).join(', ');
        toast.error(`This text contains banned words: ${words}. Please rephrase.`, { duration: 6000 });
        return;
      }
    } catch (e) {
      // If the check endpoint fails, fall through — backend will block on save anyway
      console.warn('Content check failed:', e);
    }
    setScriptGenerations(prev => prev.map((gen, i) => {
      if (i !== genIdx) return gen;
      const newSections = gen.sections.map((s, j) => j === secIdx ? { ...s, text: newText } : s);
      return {
        ...gen,
        sections: newSections,
        script: newSections.map(s => s.text).join('\n\n'),
      };
    }));
    // Clear any stale warnings for this section
    setSectionWarnings(prev => {
      const sectionName = scriptGenerations[genIdx]?.sections?.[secIdx]?.name;
      if (!sectionName) return prev;
      const { [sectionName]: _removed, ...rest } = prev;
      return rest;
    });
    setEditingSection(null);
    setEditDraft("");
    toast.success("Section updated");
  };


  // Save script to database
  const saveScript = async (title, script, sections, propertyDescription) => {
    try {
      setIsSaving(true);
      await axios.post(`${API}/scripts/save`, {
        title: title,
        property_description: propertyDescription,
        final_script: script,
        sections: sections,
        settings: {
          presenter_name: presenterName,
          agency_name: agencyName,
          town_name: townName,
          key_sales_point: keySalesPoint,
          agency_style: agencyStyle,
          agent_personality: agentPersonality,
          script_length: scriptLength,
          selected_rooms: []
        }
      });
      toast.success('Script saved to history');
    } catch (error) {
      console.error('Failed to save script:', error);
      // Don't show error toast for auto-save - it's not critical
    } finally {
      setIsSaving(false);
    }
  };

  // Load script from history
  const handleLoadScript = (savedScript) => {
    // Load the script settings
    setPresenterName(savedScript.settings?.presenter_name || "");
    setAgencyName(savedScript.settings?.agency_name || "");
    setTownName(savedScript.settings?.town_name || "");
    setKeySalesPoint(savedScript.settings?.key_sales_point || "");
    setAgencyStyle(savedScript.settings?.agency_style || "");
    setAgentPersonality(savedScript.settings?.agent_personality || "");
    setScriptLength(savedScript.settings?.script_length || "Standard (60–90 seconds)");
    
    // Load the property description
    if (savedScript.property_description) {
      setDescriptionTabs([{
        propertyId: 'loaded',
        description: savedScript.property_description
      }]);
      setActiveDescriptionTab(0);
    }
    
    // Load the generated script as first version
    setScriptGenerations([{
      id: savedScript.id || Date.now(),
      script: savedScript.final_script,
      sections: savedScript.sections || [],
      timestamp: new Date(savedScript.created_at).toLocaleTimeString(),
      title: savedScript.title,
      savedAt: savedScript.created_at
    }]);
    setActiveTab(0);
    setScriptTitle(savedScript.title);
    setLoadedScriptId(savedScript.id);
    
    setShowHistory(false);
    toast.success('Script loaded - tweak settings and generate new versions!');
    
    // On mobile, scroll to the output
    if (window.innerWidth < 1024) {
      setTimeout(() => {
        const outputSection = document.querySelector('.output-section');
        if (outputSection) {
          outputSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  };

  // Clear loaded state when starting fresh
  const handleStartNew = () => {
    setScriptGenerations([]);
    setLoadedScriptId(null);
    setScriptTitle("");
    setDescriptionTabs([]);
    setActiveDescriptionTab(0);
    setPresenterName("");
    setAgencyName("");
    setTownName("");
    setKeySalesPoint("");
    setAgencyStyle("");
    setAgentPersonality("");
    setInputMode("rightmove");
    setRightmoveUrl("");
    toast.success('Starting fresh - ready for a new property!');
  };

  const handleGenerate = async () => {
    if (!keySalesPoint.trim()) {
      toast.error("Please enter a key sales point");
      return;
    }
    
    // Get the active description
    const currentDescription = descriptionTabs.length > 0 
      ? descriptionTabs[activeDescriptionTab]?.description || ""
      : "";
      
    if (!currentDescription.trim()) {
      toast.error("Please import a property description first");
      return;
    }
    if (!agencyStyle) {
      toast.error("Please select an agency style");
      return;
    }
    if (!agentPersonality) {
      toast.error("Please select an agent personality");
      return;
    }

    // Check if title is provided
    if (!scriptTitle.trim()) {
      toast.error("Please enter a property title/address");
      // Focus the title input
      document.getElementById('script-title-input')?.focus();
      return;
    }

    // Check if we've reached the max of 5 generations
    if (scriptGenerations.length >= 5) {
      toast.error("Maximum of 5 script generations reached. Please delete one to generate a new script.");
      return;
    }

    setIsGenerating(true);
    
    // On narrow screens (stacked layout), don't scroll - we'll scroll to output after generation
    // On wide screens, scroll to top to show the loader
    const isStackedLayout = window.innerWidth < 1024;
    if (!isStackedLayout) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
    
    try {
      // Start the async job — returns immediately with a job_id, then we poll.
      // The old sync endpoint hit production's ~30s gateway timeout on long
      // descriptions; the start/status pair avoids that entirely.
      const startRes = await axios.post(`${API}/generate-script/start`, {
        presenter_name: includePresenter ? presenterName : "",
        agency_name: includeAgency ? agencyName : "",
        town_name: includeTown ? townName : "",
        key_sales_point: keySalesPoint,
        property_description: currentDescription,
        agency_style: agencyStyle,
        agent_personality: agentPersonality,
        script_length: scriptLength,
        selected_rooms: [], // Empty - backend will auto-detect rooms from description
      });
      const jobId = startRes.data.job_id;

      // Poll status every 2s, up to 3 minutes (way longer than any realistic
      // pipeline run). If something blocks past that, surface a clear timeout.
      const POLL_INTERVAL_MS = 2000;
      const POLL_MAX_MS = 180000;
      const startedAt = Date.now();
      let result = null;
      while (!result) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        const statusRes = await axios.get(`${API}/generate-script/status/${jobId}`);
        const job = statusRes.data;
        if (job.status === "done") {
          result = job.result;
          break;
        }
        if (job.status === "error") {
          throw new Error(job.error || "Script generation failed");
        }
        if (Date.now() - startedAt > POLL_MAX_MS) {
          throw new Error("Script generation is taking longer than expected. Please try again.");
        }
      }
      const response = { data: result };

      const newGeneration = {
        id: Date.now(),
        script: response.data.final_script,
        sections: response.data.sections || [],
        timestamp: new Date().toLocaleTimeString(),
        title: scriptTitle,
      };

      setScriptGenerations((prev) => [...prev, newGeneration]);
      setActiveTab(scriptGenerations.length); // Switch to the new tab
      toast.success("Script generated successfully!");
      
      // On stacked layout, scroll to the output section after a short delay
      if (isStackedLayout) {
        setTimeout(() => {
          const outputSection = document.querySelector('.output-section');
          if (outputSection) {
            outputSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      }
      
      // Auto-save to database
      saveScript(scriptTitle, response.data.final_script, response.data.sections || [], currentDescription);
      
    } catch (error) {
      console.error("Error generating script:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to generate script. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeleteGeneration = (index, e) => {
    e.stopPropagation();
    setScriptGenerations((prev) => prev.filter((_, i) => i !== index));
    if (activeTab >= index && activeTab > 0) {
      setActiveTab(activeTab - 1);
    }
    toast.success("Script deleted");
  };

  const handleFetchDescription = async () => {
    if (!rightmoveUrl.trim()) {
      toast.error("Please enter a Rightmove URL");
      return;
    }

    // Check if we've reached the max of 5 descriptions
    if (descriptionTabs.length >= 5) {
      toast.error("Maximum of 5 property descriptions reached. Please delete one to import a new property.");
      return;
    }

    // Extract property ID from URL
    const match = rightmoveUrl.match(/properties\/(\d+)/);
    if (!match) {
      toast.error("Invalid Rightmove URL. Please check the URL format.");
      return;
    }

    const propertyId = match[1];
    
    // Check if this property ID already exists
    const existingTab = descriptionTabs.find(tab => tab.propertyId === propertyId);
    if (existingTab) {
      const existingIndex = descriptionTabs.indexOf(existingTab);
      setActiveDescriptionTab(existingIndex);
      toast.info("This property is already imported. Switched to that tab.");
      return;
    }

    setIsFetchingDescription(true);

    try {
      const response = await axios.post(`${API}/fetch-rightmove-description`, {
        property_id: propertyId,
      });

      const newDescription = {
        id: Date.now(),
        propertyId: propertyId,
        description: response.data.description,
        url: rightmoveUrl,
        title: response.data.title,
      };

      setDescriptionTabs((prev) => [...prev, newDescription]);
      setActiveDescriptionTab(descriptionTabs.length); // Switch to the new tab
      
      // Auto-fill the script title with the property address/title from Rightmove
      if (response.data.title && !scriptTitle) {
        setScriptTitle(response.data.title);
      }

      // Auto-fill key sales point if returned and field is empty
      if (response.data.key_sales_point && !keySalesPoint) {
        setKeySalesPoint(response.data.key_sales_point);
        toast.success("Property imported - title & key sales point auto-filled!");
      } else if (response.data.title && !scriptTitle) {
        toast.success("Property imported - title auto-filled!");
      } else {
        toast.success("Property description imported!");
      }
      
      setRightmoveUrl(""); // Clear the URL input
    } catch (error) {
      console.error("Error fetching description:", error);
      const errorMessage = error.response?.data?.detail || "Failed to fetch description. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsFetchingDescription(false);
    }
  };

  const handleFetchWebsiteDescription = async () => {
    if (!websiteUrl.trim()) {
      toast.error("Please enter a website URL");
      return;
    }

    if (descriptionTabs.length >= 5) {
      toast.error("Maximum of 5 property descriptions reached. Please delete one to import a new property.");
      return;
    }

    setIsFetchingDescription(true);

    try {
      const response = await axios.post(`${API}/fetch-website-description`, {
        url: websiteUrl,
      });

      const newDescription = {
        id: Date.now(),
        propertyId: `web-${Date.now()}`,
        description: response.data.description,
        url: websiteUrl,
        title: response.data.title,
      };

      setDescriptionTabs((prev) => [...prev, newDescription]);
      setActiveDescriptionTab(descriptionTabs.length);

      if (response.data.title && !scriptTitle) {
        setScriptTitle(response.data.title);
      }

      if (response.data.key_sales_point && !keySalesPoint) {
        setKeySalesPoint(response.data.key_sales_point);
        toast.success("Property imported - title & key sales point auto-filled!");
      } else if (response.data.title && !scriptTitle) {
        toast.success("Property imported - title auto-filled!");
      } else {
        toast.success("Property description imported!");
      }

      setWebsiteUrl("");
    } catch (error) {
      console.error("Error fetching website description:", error);
      const errorMessage = error.response?.data?.detail || "Failed to extract property details. Try pasting the description manually.";
      toast.error(errorMessage);
    } finally {
      setIsFetchingDescription(false);
    }
  };

  const handleDeleteDescriptionTab = (index, e) => {
    e.stopPropagation();
    setDescriptionTabs((prev) => prev.filter((_, i) => i !== index));
    if (activeDescriptionTab >= index && activeDescriptionTab > 0) {
      setActiveDescriptionTab(activeDescriptionTab - 1);
    }
    toast.success("Property description deleted");
  };

  const handleCopy = async (script) => {
    if (!script) {
      toast.error("No script to copy");
      return;
    }

    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(script);
        toast.success("Script copied to clipboard!");
        return;
      }
    } catch (err) {
      console.log("Clipboard API failed, trying fallback:", err);
    }

    // Fallback method for non-HTTPS or when clipboard API fails
    try {
      const textArea = document.createElement("textarea");
      textArea.value = script;
      textArea.style.position = "absolute";
      textArea.style.left = "-9999px";
      textArea.style.top = "0";
      textArea.setAttribute('readonly', '');
      document.body.appendChild(textArea);
      
      // Select the text
      if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
        // iOS specific handling
        const range = document.createRange();
        range.selectNodeContents(textArea);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        textArea.setSelectionRange(0, 999999);
      } else {
        textArea.select();
        textArea.setSelectionRange(0, 999999);
      }
      
      // Copy the text
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success("Script copied to clipboard!");
      } else {
        toast.error("Failed to copy. Please copy manually.");
      }
    } catch (err) {
      console.error("Copy failed:", err);
      toast.error("Failed to copy. Please copy manually.");
    }
  };

  return (
    <div className="App">
      <div className="container">
        {/* Compact Header Bar */}
        <AppHeader onBack={onBack} />
        
        {/* Script Mode Toggle */}
        <div className="script-mode-toggle" data-testid="script-mode-toggle">
          <button
            className={`script-mode-btn ${scriptMode === 'property' ? 'mode-active' : ''}`}
            onClick={() => setScriptMode('property')}
            data-testid="script-mode-property"
          >
            <Home size={16} />
            Property Tour
          </button>
          <button
            className={`script-mode-btn ${scriptMode === 'market-update' ? 'mode-active' : ''} ${!isStaffUser ? 'mode-soon' : ''}`}
            onClick={() => isStaffUser && setScriptMode('market-update')}
            disabled={!isStaffUser}
            aria-disabled={!isStaffUser}
            title={isStaffUser ? undefined : "Market Update scripts are coming soon"}
            data-testid="script-mode-market"
          >
            <Sparkles size={16} />
            Market Update
            {!isStaffUser && <span className="script-mode-soon" data-testid="script-mode-market-soon">Coming soon</span>}
          </button>
        </div>

        {scriptMode === 'market-update' ? (
          <MarketUpdateBuilder />
        ) : showGate ? (
          <OrderGate
            data={spData}
            loading={spLoading}
            onReload={reloadSpOrders}
            onHistory={() => setShowHistory(true)}
            canSkip={isStaffUser}
            onSkip={() => setSkipGate(true)}
            onPick={(o) => { setSelectedWmsOrder(o); if (!scriptTitle) setScriptTitle(o.address || ""); }}
          />
        ) : (
        <>
        {/* Script Nav */}
        <div className="script-nav-bar">
          <button 
            onClick={() => setShowHistory(true)} 
            className="previous-scripts-btn"
            data-testid="previous-scripts-btn"
          >
            <History size={16} />
            Previous Scripts
          </button>
          {(scriptGenerations.length > 0 || loadedScriptId) && (
            <button 
              onClick={handleStartNew} 
              className="start-new-btn"
              data-testid="start-new-btn"
            >
              <Plus size={16} />
              New Property
            </button>
          )}
        </div>

        <div className="content-wrapper">
          <div className="form-section">
            {/* Which WMS property (Social Pro package) is this script for? */}
            <SocialProOrderPicker
              data={spData}
              loading={spLoading}
              onReload={reloadSpOrders}
              value={selectedWmsOrder}
              onChange={(o) => {
                setSelectedWmsOrder(o);
                if (o && !scriptTitle) setScriptTitle(o.address || "");
              }}
            />

            {/* Import Mode Selection */}
            <div className="form-group import-mode-section">
              <Label className="form-label">How would you like to add the property?</Label>
              <div className="import-mode-toggle">
                <button
                  type="button"
                  className={`mode-button rightmove-mode ${inputMode === 'rightmove' ? 'mode-active' : ''}`}
                  onClick={() => {
                    if (inputMode !== 'rightmove') {
                      setDescriptionTabs([]);
                      setActiveDescriptionTab(0);
                    }
                    setInputMode('rightmove');
                  }}
                >
                  <Home size={18} className="mode-icon" />
                  Rightmove
                </button>
                <button
                  type="button"
                  className={`mode-button website-mode ${inputMode === 'website' ? 'mode-active' : ''}`}
                  onClick={() => {
                    if (inputMode !== 'website') {
                      setDescriptionTabs([]);
                      setActiveDescriptionTab(0);
                    }
                    setInputMode('website');
                  }}
                  data-testid="website-mode-btn"
                >
                  <Globe size={18} className="mode-icon" />
                  Website Link
                </button>
                <button
                  type="button"
                  className={`mode-button ${inputMode === 'manual' ? 'mode-active' : ''}`}
                  onClick={() => {
                    // Clear any Rightmove/Website tabs so manual mode always
                    // writes to a fresh single tab and Generate reads the
                    // same tab (activeDescriptionTab=0). Prevents stale
                    // cross-mode descriptions from bombing generation.
                    if (inputMode !== 'manual') {
                      setDescriptionTabs([]);
                      setActiveDescriptionTab(0);
                    }
                    setInputMode('manual');
                  }}
                >
                  <Plus size={18} className="mode-icon" />
                  Enter Manually
                </button>
              </div>
            </div>

            {/* Rightmove URL - shown when rightmove mode */}
            {inputMode === 'rightmove' && (
              <div className="form-group">
                <Label htmlFor="rightmove-url" className="form-label">
                  Rightmove Property URL
                </Label>
                <div className="url-input-group">
                  <input
                    id="rightmove-url"
                    data-testid="rightmove-url-input"
                    placeholder="https://www.rightmove.co.uk/properties/167548442"
                    value={rightmoveUrl}
                    onChange={(e) => setRightmoveUrl(e.target.value)}
                    className="url-input"
                    type="url"
                  />
                  {rightmoveUrl.trim() && (
                    <Button
                      onClick={handleFetchDescription}
                      disabled={isFetchingDescription}
                      className="fetch-button"
                      data-testid="fetch-description-button"
                      type="button"
                    >
                      {isFetchingDescription ? (
                        <>
                          <Loader2 className="animate-spin button-icon" />
                          Importing...
                        </>
                      ) : (
                        "Import Property"
                      )}
                    </Button>
                  )}
                </div>
                <span className="field-hint">
                  {rightmoveUrl.trim() ? 'Click Import Property to fetch the listing details' : 'Paste the full Rightmove URL to import property details'}
                </span>
              </div>
            )}

            {/* Website URL - shown when website mode */}
            {inputMode === 'website' && (
              <div className="form-group">
                <Label htmlFor="website-url" className="form-label">
                  Property Listing URL
                </Label>
                <div className="url-input-group">
                  <input
                    id="website-url"
                    data-testid="website-url-input"
                    placeholder="https://www.youragency.co.uk/property/123-high-street"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    className="url-input"
                    type="url"
                  />
                  {websiteUrl.trim() && (
                    <Button
                      onClick={handleFetchWebsiteDescription}
                      disabled={isFetchingDescription}
                      className="fetch-button"
                      data-testid="fetch-website-button"
                      type="button"
                    >
                      {isFetchingDescription ? (
                        <>
                          <Loader2 className="animate-spin button-icon" />
                          Scraping...
                        </>
                      ) : (
                        "Import Property"
                      )}
                    </Button>
                  )}
                </div>
                <span className="field-hint">
                  {websiteUrl.trim() ? 'Click Import Property to scrape the listing details' : 'Paste any estate agent property page URL'}
                </span>
              </div>
            )}

            {/* Manual Entry - shown when manual mode */}
            {inputMode === 'manual' && (
              <>
                <div className="form-group title-section">
                  <Label htmlFor="script-title" className="form-label required-label">
                    Property Title / Address *
                  </Label>
                  <input
                    id="script-title-input"
                    data-testid="script-title-input"
                    placeholder="e.g., 14 Maple Grove, Richmond, Surrey TW10 6HQ"
                    value={scriptTitle}
                    onChange={(e) => setScriptTitle(e.target.value)}
                    className="text-input title-input"
                    type="text"
                    required
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="manual-description" className="form-label">
                    Property Description *
                  </Label>
                  <Textarea
                    id="manual-description"
                    data-testid="manual-description-input"
                    placeholder="Paste the property description here..."
                    value={descriptionTabs[0]?.description || ""}
                    onChange={(e) => {
                      if (descriptionTabs.length === 0) {
                        setDescriptionTabs([{ id: Date.now(), propertyId: 'manual', description: e.target.value }]);
                        setActiveDescriptionTab(0);
                      } else {
                        const updated = [...descriptionTabs];
                        updated[0] = { ...updated[0], description: e.target.value };
                        setDescriptionTabs(updated);
                      }
                    }}
                    className="description-textarea manual-textarea"
                    rows={6}
                  />
                </div>
              </>
            )}

            {/* Property Title - shown after Rightmove/Website import (read-only display or editable) */}
            {(inputMode === 'rightmove' || inputMode === 'website') && descriptionTabs.length > 0 && (
              <div className="form-group title-section imported-title">
                <Label htmlFor="script-title" className="form-label">
                  Property Title
                </Label>
                <input
                  id="script-title-input"
                  data-testid="script-title-input"
                  placeholder="Auto-filled from Rightmove"
                  value={scriptTitle}
                  onChange={(e) => setScriptTitle(e.target.value)}
                  className="text-input title-input"
                  type="text"
                />
                <span className="field-hint">Auto-filled from listing. You can edit if needed.</span>
              </div>
            )}

            {/* Presenter Section - with checkboxes */}
            <div className="form-group presenter-section">
              <Label className="form-label section-label">Script Personalization</Label>
              <div className="presenter-options">
                <div className={`presenter-option ${!includePresenter ? 'option-disabled' : ''}`}>
                  <div className="option-header">
                    <Checkbox 
                      id="include-presenter"
                      checked={includePresenter}
                      onCheckedChange={setIncludePresenter}
                    />
                    <Label htmlFor="include-presenter" className="option-label">Your Name</Label>
                  </div>
                  <input
                    id="presenter-name"
                    data-testid="presenter-name-input"
                    value={presenterName}
                    onChange={(e) => setPresenterName(e.target.value)}
                    className="text-input"
                    type="text"
                    placeholder="Agent name"
                  />
                </div>
                <div className={`presenter-option ${!includeAgency ? 'option-disabled' : ''}`}>
                  <div className="option-header">
                    <Checkbox 
                      id="include-agency"
                      checked={includeAgency}
                      onCheckedChange={setIncludeAgency}
                    />
                    <Label htmlFor="include-agency" className="option-label">Agency Name</Label>
                  </div>
                  <input
                    id="agency-name"
                    data-testid="agency-name-input"
                    value={agencyName}
                    onChange={(e) => setAgencyName(e.target.value)}
                    className="text-input"
                    type="text"
                    placeholder="Agency name"
                  />
                </div>
                <div className={`presenter-option ${!includeTown ? 'option-disabled' : ''}`}>
                  <div className="option-header">
                    <Checkbox 
                      id="include-town"
                      checked={includeTown}
                      onCheckedChange={setIncludeTown}
                    />
                    <Label htmlFor="include-town" className="option-label">Town</Label>
                  </div>
                  <input
                    id="town-name"
                    data-testid="town-name-input"
                    value={townName}
                    onChange={(e) => setTownName(e.target.value)}
                    className="text-input"
                    type="text"
                    placeholder="Town name"
                  />
                </div>
              </div>
            </div>

            {/* Key Sales Point */}
            <div className="form-group">
              <Label htmlFor="key-sales-point" className="form-label">
                Key Sales Point
              </Label>
              <Textarea
                id="key-sales-point"
                data-testid="key-sales-point-input"
                placeholder="e.g., Stunning period home in Richmond with south-facing garden"
                value={keySalesPoint}
                onChange={(e) => setKeySalesPoint(e.target.value)}
                className="textarea-input"
                rows={2}
              />
            </div>

            {/* Property Descriptions - Only show for Rightmove/Website mode when there are imported descriptions */}
            {(inputMode === 'rightmove' || inputMode === 'website') && descriptionTabs.length > 0 && (
              <div className="form-group">
                <div className="description-header">
                  <Label className="form-label">
                    Imported Property Descriptions
                  </Label>
                </div>
                
                <Tabs value={activeDescriptionTab.toString()} onValueChange={(val) => setActiveDescriptionTab(parseInt(val))}>
                  <TabsList className="description-tabs-list">
                    {descriptionTabs.map((desc, index) => (
                      <TabsTrigger 
                        key={desc.id} 
                        value={index.toString()}
                        className="description-tab-trigger"
                        data-testid={`description-tab-${index + 1}`}
                      >
                        <span>{desc.isManual ? `Description ${index + 1}` : `Property ${desc.propertyId}`}</span>
                        <button
                          onClick={(e) => handleDeleteDescriptionTab(index, e)}
                          className="tab-delete"
                          data-testid={`delete-description-tab-${index + 1}`}
                        >
                          <X className="tab-delete-icon" />
                        </button>
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  
                  {descriptionTabs.map((desc, index) => (
                    <TabsContent key={desc.id} value={index.toString()} className="description-tab-content">
                      <Textarea
                        id="property-desc"
                        data-testid="property-description-input"
                        value={desc.description}
                        onChange={(e) => {
                          const newTabs = [...descriptionTabs];
                          newTabs[index].description = e.target.value;
                          setDescriptionTabs(newTabs);
                        }}
                        placeholder="Paste or type the property description here..."
                        className={`textarea-input ${!desc.isManual ? 'description-readonly' : ''}`}
                        readOnly={!desc.isManual}
                        rows={6}
                      />
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            )}

            {/* Script Length */}
            <div className="form-group">
              <Label className="form-label">Script Length</Label>
              <div className="script-length-grid">
                <button
                  type="button"
                  data-testid="length-short"
                  onClick={() => setScriptLength("Short (30–45 seconds)")}
                  className={`script-length-button ${scriptLength === "Short (30–45 seconds)" ? 'script-length-selected' : ''}`}
                >
                  <Clock className="script-length-icon" />
                  <span className="script-length-time">30–45s</span>
                  <span className="script-length-label">Short</span>
                </button>
                <button
                  type="button"
                  data-testid="length-standard"
                  onClick={() => setScriptLength("Standard (60–90 seconds)")}
                  className={`script-length-button ${scriptLength === "Standard (60–90 seconds)" ? 'script-length-selected' : ''}`}
                >
                  <Clock3 className="script-length-icon" />
                  <span className="script-length-time">60–90s</span>
                  <span className="script-length-label">Standard</span>
                </button>
                <button
                  type="button"
                  data-testid="length-extended"
                  onClick={() => setScriptLength("Extended (2–3 minutes)")}
                  className={`script-length-button ${scriptLength === "Extended (2–3 minutes)" ? 'script-length-selected' : ''}`}
                >
                  <Clock9 className="script-length-icon" />
                  <span className="script-length-time">2–3 min</span>
                  <span className="script-length-label">Extended</span>
                </button>
              </div>
            </div>

            {/* Generate Button */}
            <div className="generate-button-wrapper">
              <Button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="generate-button"
                data-testid="generate-button"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Generating Script...
                  </>
                ) : scriptGenerations.length > 0 ? (
                  <>
                    <Plus size={18} />
                    Generate New Version
                  </>
                ) : (
                  "Generate Script"
                )}
              </Button>
            </div>
          </div>

          {/* Output Section - always rendered so the preview column is ready */}
          <div className="output-section" data-testid="property-tour-output">
            <div className="output-header">
              <Label className="output-label">
                {scriptTitle ? `Scripts for: ${scriptTitle}` : 'Generated Video Tour Scripts'}
              </Label>
              {scriptGenerations.length > 0 && (
                <span className="version-count">{scriptGenerations.length} version{scriptGenerations.length !== 1 ? 's' : ''}</span>
              )}
            </div>

            {isGenerating ? (
              <div className="output-content" data-testid="script-output">
                <div className="loading-state">
                  <Loader2 className="animate-spin loading-icon" />
                  <p>Generating your script...</p>
                  <p className="loading-subtext">This may take 30-60 seconds</p>
                </div>
              </div>
            ) : scriptGenerations.length === 0 ? (
              <div className="output-empty-state" data-testid="script-output-empty">
                <Sparkles className="output-empty-icon" size={28} />
                <h3>Your script will appear here</h3>
                <p>Import a property, fill in the details, and hit generate</p>
              </div>
            ) : (
                <Tabs value={activeTab.toString()} onValueChange={(val) => setActiveTab(parseInt(val))}>
                  {/* Only show tabs if more than 1 script version */}
                  {scriptGenerations.length > 1 && (
                    <TabsList className="tabs-list">
                      {scriptGenerations.map((gen, index) => (
                        <TabsTrigger 
                          key={`tab-${gen.id}`} 
                          value={index.toString()}
                          className="tab-trigger"
                          data-testid={`script-tab-${index + 1}`}
                        >
                          <span>Version {index + 1}</span>
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => handleDeleteGeneration(index, e)}
                            onKeyDown={(e) => e.key === 'Enter' && handleDeleteGeneration(index, e)}
                            className="tab-delete"
                            data-testid={`delete-tab-${index + 1}`}
                          >
                            <X className="tab-delete-icon" />
                          </span>
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  )}
                  
                  {scriptGenerations.map((gen, index) => (
                    <TabsContent key={`content-${gen.id}`} value={index.toString()} className="tab-content">
                      <div className="script-header">
                        <span className="script-timestamp">Generated at {gen.timestamp}</span>
                        <div className="script-header-actions">
                          {confirmedScriptIndex === index ? (
                            <div className="confirmed-badge" data-testid="script-confirmed-badge">
                              <Check size={16} />
                              Script Confirmed
                              {selectedAvatar && <AvatarChip avatar={selectedAvatar} size={20} />}
                            </div>
                          ) : (
                            <Button
                              onClick={() => setShowConfirmDialog(true)}
                              size="sm"
                              className="confirm-script-button"
                              data-testid="confirm-script-button"
                              disabled={isSubmitting || editingSection !== null}
                              title={editingSection ? 'Save your edits before confirming' : 'Confirm and send to editors'}
                            >
                              {isSubmitting ? (
                                <>
                                  <Loader2 className="animate-spin icon" />
                                  Submitting...
                                </>
                              ) : (
                                <>
                                  <SendHorizontal className="icon" />
                                  Confirm Script
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Script Sections - Editable cards (agents can refine each section before confirming) */}
                      {gen.sections && gen.sections.length > 0 && (
                        <div className="script-sections-detailed">
                          {gen.sections.map((section, sIdx) => {
                            const IconComponent = getSectionIcon(section.icon);
                            const sectionId = `${index}-${sIdx}`;
                            const isEditing = editingSection === sectionId;
                            const isConfirmed = confirmedScriptIndex === index;
                            const warnedWords = sectionWarnings[section.name];
                            return (
                              <div
                                key={sIdx}
                                className={`script-section-card ${warnedWords ? 'section-card-warning' : ''}`}
                              >
                                <div className="section-card-header">
                                  <div className="section-card-icon">
                                    <IconComponent size={28} />
                                  </div>
                                  <div className="section-card-info">
                                    <h4 className="section-card-title">{section.name}</h4>
                                  </div>
                                  {!isConfirmed && !isEditing && (
                                    <button
                                      className="section-edit-btn"
                                      onClick={() => startEditSection(index, sIdx, section.text)}
                                      data-testid={`edit-section-${sIdx}`}
                                      title="Edit section"
                                    >
                                      <Pencil size={14} />
                                      <span>Edit</span>
                                    </button>
                                  )}
                                  <button
                                    className="section-copy-btn"
                                    onClick={() => copyToClipboard(section.text, sectionId)}
                                    data-testid={`copy-section-${sIdx}`}
                                    title="Copy section"
                                  >
                                    {copiedSection === sectionId ? <ClipboardCheck size={15} /> : <Copy size={15} />}
                                  </button>
                                </div>
                                {warnedWords && (
                                  <div className="section-card-warning-banner">
                                    <AlertTriangle size={13} />
                                    <span>Contains blocked language: <strong>{warnedWords.join(', ')}</strong></span>
                                  </div>
                                )}
                                <div className="section-card-content">
                                  {isEditing ? (
                                    <div className="section-edit-area">
                                      <Textarea
                                        value={editDraft}
                                        onChange={e => setEditDraft(e.target.value)}
                                        rows={Math.max(3, Math.min(10, editDraft.split('\n').length + 1))}
                                        className="section-edit-textarea"
                                        autoFocus
                                        data-testid={`edit-textarea-${sIdx}`}
                                      />
                                      <div className="section-edit-actions">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={cancelEditSection}
                                          data-testid={`cancel-edit-${sIdx}`}
                                        >
                                          Cancel
                                        </Button>
                                        <Button
                                          size="sm"
                                          onClick={() => saveSectionEdit(index, sIdx)}
                                          data-testid={`save-edit-${sIdx}`}
                                        >
                                          Save
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="section-text">{section.text}</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}

                          <button
                            className="copy-all-btn"
                            onClick={() => copyAllSections(gen.sections)}
                            data-testid="copy-all-script"
                          >
                            {copiedSection === 'all' ? <ClipboardCheck size={16} /> : <Copy size={16} />}
                            {copiedSection === 'all' ? 'Copied!' : 'Copy Full Script'}
                          </button>
                        </div>
                      )}
                      
                      {/* Confirmation Dialog — choose presenter avatar, then send */}
                      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                        <AlertDialogContent className="confirm-script-dialog" data-testid="confirm-script-dialog">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Confirm Script Submission</AlertDialogTitle>
                            <AlertDialogDescription>
                              Choose who presents this video, then send the script to the editors for production.
                            </AlertDialogDescription>
                          </AlertDialogHeader>

                          <div className="confirm-dialog-body">
                            <div className="confirm-property-row" data-testid="confirm-property-row">
                              <Home size={14} />
                              <span className="confirm-property-title">{scriptTitle || selectedWmsOrder?.address || gen.title || "Untitled Property"}</span>
                              {selectedWmsOrder ? (
                                <span className="confirm-wms-pill">WMS #{selectedWmsOrder.order_id}{selectedWmsOrder.matched_product ? ` · ${selectedWmsOrder.matched_product}` : ""}</span>
                              ) : gatingOn ? (
                                <span className="confirm-wms-pill warn"><AlertTriangle size={11} /> Pick a Social Pro property first</span>
                              ) : null}
                            </div>
                            <div className="avp-light">
                              <AvatarPicker value={selectedAvatar?.id} onChange={setSelectedAvatar} />
                            </div>
                            {selectedAvatar && (
                              <div className="confirm-selected-row">
                                <span>Presenter:</span> <AvatarChip avatar={selectedAvatar} />
                              </div>
                            )}
                          </div>

                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={(e) => { e.preventDefault(); submitOrder(gen); }}
                              className="confirm-action-button"
                              disabled={isSubmitting || !selectedAvatar || needsOrder}
                              data-testid="confirm-submit-btn"
                            >
                              {isSubmitting ? 'Sending…' : (selectedAvatar ? `Confirm with ${selectedAvatar.name}` : 'Choose a presenter')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TabsContent>
                  ))}
                </Tabs>
              )}
          </div>
        </div>
        </>
        )}
      </div>
      
      {/* Script History Panel */}
      {showHistory && (
        <ScriptHistory 
          onSelectScript={handleLoadScript}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}

export default ScriptBuilder;
